import {
  OVERWORK_IDENTIFIERS,
  PAUSE_IDENTIFIERS,
} from '@/modules/work-infringements/work-day-category-labels'
import type { CombinedInfringementSource } from '../table/combined-rows'

export interface CategoryGroup {
  key: string
  source: CombinedInfringementSource
  identifiers: string[]
}

export type CategoryGroupKey =
  | 'B'
  | 'C'
  | 'D'
  | 'W-overwork'
  | 'W-pause'
  | 'T-country'
  | 'T-manual'

/** The rule groups each infringement type can be narrowed to. Driving and rest
 * time keeps its B/C/D families; the others group rules by what the driver has
 * to do about them. */
export function categoryGroups(
  drivingRestIdentifiers: Record<'B' | 'C' | 'D', string[]>
): Record<
  CombinedInfringementSource,
  (CategoryGroup & { key: CategoryGroupKey })[]
> {
  return {
    DRIVING_REST: (['B', 'C', 'D'] as const).map(key => ({
      key,
      source: 'DRIVING_REST',
      identifiers: drivingRestIdentifiers[key],
    })),
    WORKING_TIME: [
      {
        key: 'W-overwork',
        source: 'WORKING_TIME',
        // W8 (the annual overtime cap) is overwork too, though the work day
        // page shows its own identifier for it.
        identifiers: [...OVERWORK_IDENTIFIERS, 'W8'],
      },
      {
        key: 'W-pause',
        source: 'WORKING_TIME',
        identifiers: [...PAUSE_IDENTIFIERS],
      },
    ],
    TACHOGRAPH_USE: [
      {
        key: 'T-country',
        source: 'TACHOGRAPH_USE',
        identifiers: ['T1', 'T2', 'T5'],
      },
      { key: 'T-manual', source: 'TACHOGRAPH_USE', identifiers: ['T3', 'T4'] },
    ],
  }
}

/** The groups picked in the filter. Nothing picked means every group, which is
 * also how the filter stores "all": as no excluded identifiers. */
export function selectedGroupKeys(
  groups: CategoryGroup[],
  excludeCategories: string[]
): string[] {
  const excluded = new Set(excludeCategories)
  const isExcluded = (group: CategoryGroup) =>
    group.identifiers.every(identifier => excluded.has(identifier))
  if (!groups.some(isExcluded)) return []
  return groups.filter(group => !isExcluded(group)).map(group => group.key)
}

/** The identifiers to leave out for a selection of groups. */
export function excludedIdentifiers(
  groups: CategoryGroup[],
  selectedKeys: string[]
): string[] {
  if (selectedKeys.length === 0) return []
  const selected = new Set(selectedKeys)
  return groups
    .filter(group => !selected.has(group.key))
    .flatMap(group => group.identifiers)
}
